import React, { useEffect, useState, useRef } from "react"
import styles from "./Sidebar.module.scss"
import { UserInterface } from "../../App"
import Dashboard from "../Dashboard/Dashboard"
import Org from "../Org/Org"
import Tasks from "../Tasks/Tasks"
import Files from "../Sessions/Chat/Files/Files"
import axios from "axios"
import { API_ROUTE } from "../../utils"
import Messaging from "../Messaging/Messaging"

interface SidebarProps {
    setUser: any
    user: UserInterface
    newSession: any
    newAppSpace: any
    messages?: any
    setMessages?: any
    activeSession?: any
    toggleLightMode: () => void
    fileTree: any
    setFileTree: (fileTree: any) => void
}

const Sidebar: React.FC<SidebarProps> = ({
    setUser,
    user,
    newSession,
    newAppSpace,
    messages,
    setMessages,
    activeSession,
    toggleLightMode,
    fileTree,
    setFileTree,
}) => {
    const [fileUploading, setFileUploading] = useState(false)
    const [chatSessions, setChatSessions] = useState<any[]>([])
    const [chatSessionsDisplayed, setChatSessionsDisplayed] = useState(5)
    const [menuState, setMenuState] = useState<{
        chatId: string | null
        x: number
        y: number
    }>({ chatId: null, x: 0, y: 0 })

    const menuRef = useRef<HTMLDivElement>(null)

    async function refreshSidebar() {
        const response = await axios.post(`${API_ROUTE}/utils/chats`, {
            userId: user._id,
            authToken: user.authToken,
        })
        if (response.status === 200) {
            setChatSessions(response.data.reverse())
        } else {
            console.error("Failed to fetch chat sessions:", response.data.message)
        }
    }

    useEffect(() => {
        if (!user._id || !user.authToken) return
        refreshSidebar()
    }, [user, messages])

    const newThread = async () => {
        setMessages({})
        newSession({
            id: Date.now(),
            name: "New Thread",
            view: "chat",
            component: null,
            send: null,
            createFile: null,
        })
    }

    // Handle rename chat session
    const handleRename = async (chatId: string, sessionName: string) => {
        const newName = window.prompt("Enter new chat name:", sessionName)
        if (newName && newName.trim()) {
            try {
                const response = await axios.post(`${API_ROUTE}/utils/rename-chat`, {
                    userId: user._id,
                    authToken: user.authToken,
                    chatId: chatId,
                    newName: newName.trim(),
                })
                if (response.data.success) {
                    await refreshSidebar() // Refresh to update UI
                } else {
                    alert("Failed to rename chat session.")
                }
            } catch (error) {
                alert("Error renaming chat session.")
            }
        }
        setMenuState({ chatId: null, x: 0, y: 0 }) // Close menu
    }

    // Handle delete chat session
    const handleDelete = async (chatId: string) => {
        const confirmed = window.confirm("Are you sure you want to delete this chat session?")
        if (confirmed) {
            try {
                const response = await axios.post(`${API_ROUTE}/utils/delete-chat`, {
                    userId: user._id,
                    authToken: user.authToken,
                    chatId: chatId,
                })
                if (response.data.success) {
                    await refreshSidebar() // Refresh to update UI
                    if (activeSession?.id === chatId) newThread()
                } else {
                    alert("Failed to delete chat session.")
                }
            } catch (error) {
                alert("Error deleting chat session.")
            }
        }
        setMenuState({ chatId: null, x: 0, y: 0 }) // Close menu
    }

    // Handle click on ellipsis to open menu
    const handleOptionsClick = (e: React.MouseEvent, chatId: string) => {
        e.stopPropagation()
        setMenuState({ chatId, x: e.clientX - 100, y: e.clientY - 40 })
    }

    // Handle mouse leave to close menu
    const handleMouseLeave = () => {
        setMenuState({ chatId: null, x: 0, y: 0 })
    }

    const SidebarItem: React.FC<{
        name: string
        icon: string
        action: () => void
        disabled?: boolean
    }> = ({ name, icon, action, disabled = false }) => {
        return (
            <div
                id={icon === "folder" ? "sidebarFiles" : undefined}
                onClick={disabled ? undefined : action}
                className={`${styles.button} ${disabled ? styles.disabled : ""}`}>
                <i className="material-symbols-rounded">{icon}</i>
                <span>{name}</span>
            </div>
        )
    }

    function formatDate() {
        const date = new Date()
        const options: Intl.DateTimeFormatOptions = {
            hour: "numeric",
            minute: "numeric",
            month: "long",
            day: "numeric",
            year: "numeric",
        }

        return date
            .toLocaleString("en-US", options)
            .replace(/(\d+)(:\d+)?\s([AP]M)/, "$1$2 $3")
            .replace(/,/, " |")
            .replace(/\b(\w{3})\w+\b/, "$1")
    }

    function logout() {
        const confirmed = window.confirm(
            "You are about to log out. Are you sure you want to continue?"
        )
        if (confirmed) {
            localStorage.removeItem("credentials")
            setUser({} as UserInterface)
            window.location.href = "#/login"
        }
    }

    return (
        <>
            <div className={styles.Sidebar}>
                <div className={styles.header}>
                    <div className={styles.flex}>
                        <div className={styles.logo}>Pretzel</div>
                        <p className={styles.date}>{formatDate()}</p>
                    </div>
                    <div className={styles.flex}>
                        <p>Integrated AI-powered cloud workspace</p>
                        <div style={{ color: "#999", cursor: "pointer" }} onClick={logout}>
                            Sign out
                        </div>
                    </div>

                    <div className={styles.grid}>
                        <div className={styles.group}>
                            user
                            <p onClick={logout}>{user.name || "Guest"}</p>
                        </div>
                        <div className={styles.group}>
                            org
                            <p
                                onClick={() =>
                                    newSession({
                                        name: "Org",
                                        component: <Org user={user} setUser={setUser} />,
                                    })
                                }>
                                ORG
                            </p>
                        </div>
                    </div>
                    <div className={styles.grid}>
                        <div
                            style={{ color: "#999", cursor: "pointer" }}
                            onClick={() => {
                                toggleLightMode()
                            }}>
                            Switch Theme
                        </div>
                    </div>
                </div>

                <br />

                <div className={styles.header}>APPS</div>

                <SidebarItem name="Home" icon="home" action={newThread} />

                <SidebarItem
                    name="Dashboard"
                    icon="dashboard"
                    action={() => {
                        newSession({
                            id: "dashboard",
                            component: <Dashboard user={user} setUser={setUser} />,
                        })
                    }}
                />

                <SidebarItem
                    name="Messaging & Email"
                    icon="mail"
                    action={() => {
                        newSession({ id: "messaging", component: <Messaging user={user} /> })
                    }}
                />

                <SidebarItem
                    name="Projects & Tasks"
                    icon="task"
                    action={() => {
                        newSession({
                            id: "tasks",
                            component: <Tasks user={user} setUser={setUser} />,
                        })
                    }}
                />
                <SidebarItem
                    name="Wiki"
                    icon="auto_stories"
                    action={() => {
                        newSession({ id: "wikis", view: "wikis" })
                    }}
                />

                <SidebarItem
                    name="Files"
                    icon="folder"
                    action={() => {
                        newSession({ view: "chat", send: "files" })
                    }}
                />

                <div className={styles.header}>ASSISTANT THREADS</div>

                <div onClick={newThread} className={styles.button}>
                    <i className="material-symbols-rounded">add</i>
                    New Thread
                </div>

                {chatSessions?.length > 0 &&
                    chatSessions
                        .slice(0, chatSessionsDisplayed)
                        .map((session: any, index: number) => (
                            <div
                                onClick={async () => {
                                    if (!session._id) {
                                        console.error("Session ID is missing:", session)
                                        return
                                    }

                                    try {
                                        const retrievedChat = await axios.post(
                                            `${API_ROUTE}/utils/get-chat`,
                                            {
                                                userId: user._id,
                                                authToken: user.authToken,
                                                chatId: session._id,
                                            }
                                        )

                                        if (!retrievedChat.data.messages) {
                                            console.error(
                                                "Failed to fetch chat session:",
                                                retrievedChat.data.message
                                            )
                                            return
                                        }

                                        setMessages(retrievedChat.data.messages || {})
                                        newSession({
                                            id: retrievedChat.data._id,
                                            name: retrievedChat.data.name,
                                            view: "chat",
                                            component: null,
                                            send: null,
                                            createFile: null,
                                        })
                                    } catch (error) {
                                        console.error("Error retrieving chat session:", error)
                                        alert("Failed to retrieve chat session.")
                                    }
                                }}
                                key={index}
                                className={`${styles.button} ${styles.session}`}
                                style={{
                                    background:
                                        activeSession?.id === session._id
                                            ? "var(--glass-2)"
                                            : "unset",
                                }}>
                                <p>{session.name}</p>
                                <div
                                    className={styles.options}
                                    onClick={(e) => handleOptionsClick(e, session._id)}>
                                    <i className="material-symbols-rounded">more_horiz</i>
                                </div>
                            </div>
                        ))}

                {chatSessions?.length > chatSessionsDisplayed && (
                    <div
                        onClick={() => {
                            setChatSessionsDisplayed(chatSessionsDisplayed + 10)
                        }}
                        className={styles.button}>
                        <i className="material-symbols-rounded">expand_more</i>
                        Show more...
                    </div>
                )}

                {menuState.chatId && (
                    <div
                        ref={menuRef}
                        className={styles.chatMenu}
                        style={{
                            top: `${menuState.y}px`,
                            left: `${menuState.x}px`,
                        }}
                        onMouseLeave={handleMouseLeave}>
                        <button
                            onClick={() => {
                                const session = chatSessions.find((s) => s._id === menuState.chatId)
                                if (menuState.chatId)
                                    handleRename(menuState.chatId, session?.name || "")
                                else console.error("No session ID to rename")
                            }}>
                            <i className="material-symbols-rounded">edit</i>
                            Rename
                        </button>
                        <button
                            onClick={() => {
                                if (menuState.chatId) handleDelete(menuState.chatId)
                                else console.error("No session ID to delete")
                            }}>
                            <i className="material-symbols-rounded">delete</i>
                            Delete
                        </button>
                    </div>
                )}

                <div className={styles.header}>CLOUD FILES</div>

                <Files
                    newAppSpace={newAppSpace}
                    setActiveAppSpace={newSession}
                    user={user}
                    setUser={setUser}
                    createFile=""
                    uploading={fileUploading}
                    setUploading={setFileUploading}
                    readMode={true}
                    fileTree={fileTree}
                    setFileTree={setFileTree}
                />
            </div>
        </>
    )
}

export default Sidebar
